import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, CATEGORY_META } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { AiMarkdown } from "../components/AiMarkdown";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Wrench, Cpu, Mail, KeyRound, ArrowLeft, ArrowRight, Loader2, RotateCcw, Sparkles } from "lucide-react";

const CATS = [
  { name: "Windows OS", icon: Cpu, symptoms: ["Blue screen / crash", "Windows Update failing", "Driver / device error", "Slow or freezing"] },
  { name: "MS Office Apps", icon: Mail, symptoms: ["Outlook won't open", "Teams camera issue", "Word/Excel crashing", "365 license expired"] },
  { name: "MS Account / Activation", icon: KeyRound, symptoms: ["Windows not activated", "Can't sign in to account", "Work/school account error", "PIN not working"] },
];
const OS_VERSIONS = ["Windows 11", "Windows 10", "Windows 11 (work/school)", "Not sure"];

const Troubleshooter = () => {
  const [step, setStep] = useState(0);
  const [category, setCategory] = useState("");
  const [symptom, setSymptom] = useState("");
  const [errorCode, setErrorCode] = useState("");
  const [osVersion, setOsVersion] = useState("Windows 11");
  const [loading, setLoading] = useState(false);
  const [resolution, setResolution] = useState("");

  const activeCat = CATS.find((c) => c.name === category);

  const reset = () => {
    setStep(0); setCategory(""); setSymptom(""); setErrorCode(""); setOsVersion("Windows 11"); setResolution("");
  };

  const submit = async () => {
    setLoading(true);
    setStep(3);
    try {
      const res = await api.post("/troubleshoot", { category, symptom, error_code: errorCode, os_version: osVersion });
      setResolution(res.data.resolution);
    } catch (e) {
      toast.error("Could not generate a resolution. Try again.");
      setResolution("⚠️ The AI service is unavailable right now. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  const steps = ["Category", "Symptom", "Details", "Resolution"];

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-6 py-10">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-11 w-11 rounded-xl bg-primary/12 border border-primary/25 grid place-items-center">
          <Wrench className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Guided Troubleshooter</h1>
          <p className="text-sm text-muted-foreground">Answer a few questions for a targeted, AI-generated fix.</p>
        </div>
      </div>

      {/* stepper */}
      <div className="flex items-center gap-2 mb-8">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center gap-2 flex-1">
            <div className={`h-8 w-8 rounded-full grid place-items-center text-xs font-bold shrink-0 transition-colors ${
              i < step ? "bg-primary text-primary-foreground" : i === step ? "bg-primary/20 text-primary border border-primary/40" : "bg-secondary text-muted-foreground"}`}>
              {i + 1}
            </div>
            {i < steps.length - 1 && <div className={`h-0.5 flex-1 rounded ${i < step ? "bg-primary" : "bg-secondary"}`} />}
          </div>
        ))}
      </div>

      <div className="glass rounded-3xl p-6 sm:p-8 min-h-[340px]">
        <AnimatePresence mode="wait">
          {/* Step 0: category */}
          {step === 0 && (
            <motion.div key="s0" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <h2 className="font-heading text-xl font-semibold mb-1">What area is the problem in?</h2>
              <p className="text-sm text-muted-foreground mb-6">Pick the closest category.</p>
              <div className="grid sm:grid-cols-3 gap-4">
                {CATS.map((c) => {
                  const meta = CATEGORY_META[c.name];
                  const active = category === c.name;
                  return (
                    <button key={c.name} onClick={() => { setCategory(c.name); setSymptom(""); }}
                      className={`rounded-2xl border p-5 text-left transition-all duration-200 hover:-translate-y-0.5 ${
                        active ? "border-primary/60 bg-primary/10" : "border-border/60 bg-secondary/30"}`}
                      data-testid={`ts-category-${meta.short.toLowerCase()}`}>
                      <div className="h-10 w-10 rounded-xl grid place-items-center mb-3"
                        style={{ background: `${meta.color}18`, border: `1px solid ${meta.color}40` }}>
                        <c.icon className="h-5 w-5" style={{ color: meta.color }} />
                      </div>
                      <p className="font-semibold text-sm">{c.name}</p>
                    </button>
                  );
                })}
              </div>
              <div className="flex justify-end mt-8">
                <Button disabled={!category} onClick={() => setStep(1)}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="ts-next-0">
                  Next <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 1: symptom */}
          {step === 1 && (
            <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <h2 className="font-heading text-xl font-semibold mb-1">Describe the symptom</h2>
              <p className="text-sm text-muted-foreground mb-5">Choose a common one or type your own.</p>
              <div className="flex flex-wrap gap-2 mb-5">
                {activeCat?.symptoms.map((s, i) => (
                  <button key={i} onClick={() => setSymptom(s)}
                    className={`text-sm px-3.5 py-2 rounded-full border transition-all ${
                      symptom === s ? "border-primary/60 bg-primary/10 text-primary" : "border-border/70 bg-secondary/40 hover:border-primary/40"}`}
                    data-testid={`ts-symptom-chip-${i}`}>
                    {s}
                  </button>
                ))}
              </div>
              <Input value={symptom} onChange={(e) => setSymptom(e.target.value)}
                placeholder="e.g. Laptop restarts randomly with a blue screen…"
                className="bg-secondary/40 border-border/70 h-12 rounded-xl" data-testid="ts-symptom-input" />
              <div className="flex justify-between mt-8">
                <Button variant="outline" onClick={() => setStep(0)} className="border-border/70" data-testid="ts-back-1">
                  <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
                </Button>
                <Button disabled={!symptom.trim()} onClick={() => setStep(2)}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="ts-next-1">
                  Next <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 2: details */}
          {step === 2 && (
            <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <h2 className="font-heading text-xl font-semibold mb-1">A few more details</h2>
              <p className="text-sm text-muted-foreground mb-5">These help us give a precise fix (error code is optional).</p>
              <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Error code (optional)</label>
              <Input value={errorCode} onChange={(e) => setErrorCode(e.target.value)}
                placeholder="e.g. 0x0000007B" className="bg-secondary/40 border-border/70 h-12 rounded-xl mt-2 mb-5 font-mono"
                data-testid="ts-errorcode-input" />
              <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Operating system</label>
              <Select value={osVersion} onValueChange={setOsVersion}>
                <SelectTrigger className="bg-secondary/40 border-border/70 h-12 rounded-xl mt-2" data-testid="ts-os-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {OS_VERSIONS.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex justify-between mt-8">
                <Button variant="outline" onClick={() => setStep(1)} className="border-border/70" data-testid="ts-back-2">
                  <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
                </Button>
                <Button onClick={submit} className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold" data-testid="ts-generate-btn">
                  <Sparkles className="h-4 w-4 mr-1.5" /> Get AI Resolution
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 3: resolution */}
          {step === 3 && (
            <motion.div key="s3" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-primary">Targeted resolution</p>
                  <h2 className="font-heading text-xl font-semibold">{symptom}</h2>
                </div>
                <Button variant="outline" onClick={reset} className="border-border/70" data-testid="ts-restart-btn">
                  <RotateCcw className="h-4 w-4 mr-1.5" /> Start over
                </Button>
              </div>
              {loading ? (
                <div className="py-16 grid place-items-center text-center">
                  <Loader2 className="h-8 w-8 text-primary animate-spin" />
                  <p className="text-sm text-muted-foreground mt-4">Analysing your {category} issue and building a fix…</p>
                </div>
              ) : (
                <div className="rounded-2xl border border-border/60 bg-secondary/30 p-5" data-testid="ts-resolution">
                  <AiMarkdown text={resolution} />
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Troubleshooter;
